// components/grounded-answer-card.js — GROUNDED ANSWER RENDERER
// ═══════════════════════════════════════════════════════════
// Phase 3 Trust — Commit B.
//
// Renders a structured grounded answer as a card with explicit trust
// signals: confidence badge, source chips, caveats, and the model used.
//
// Two card layouts:
//   - Claim: single answer + confidence + sources + caveats
//   - Analysis: summary + finding list (each with its own confidence)
//                + open questions
//
// Source chips hover-expand to show the snippet. URLs open in a new
// tab. Sources without URLs render as text-only chips so the user
// can see what was cited even when no link was reachable.
// ═══════════════════════════════════════════════════════════

import { useState, memo } from 'react';

const CONFIDENCE_COLORS = {
  high:   { color: '#c8a96e', bg: 'rgba(200,169,110,0.1)',  border: 'rgba(200,169,110,0.35)' },
  medium: { color: '#7a9e82', bg: 'rgba(122,158,130,0.1)',  border: 'rgba(122,158,130,0.35)' },
  low:    { color: '#9b8fc4', bg: 'rgba(155,143,196,0.1)',  border: 'rgba(155,143,196,0.35)' },
};

function ConfidenceBadge({ level }) {
  const palette = CONFIDENCE_COLORS[level] || CONFIDENCE_COLORS.medium;
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      fontSize: 9,
      fontFamily: 'Georgia, serif',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: palette.color,
      background: palette.bg,
      border: `1px solid ${palette.border}`,
      borderRadius: 4,
      padding: '3px 8px',
    }}>
      <span style={{
        width: 5,
        height: 5,
        borderRadius: '50%',
        background: palette.color,
        display: 'inline-block',
      }} />
      {level} confidence
    </span>
  );
}

function SourceChip({ source }) {
  const [expanded, setExpanded] = useState(false);
  // Render as a clickable link only when (a) there's a URL AND
  // (b) the server-side reachability check confirmed the URL responds.
  // Otherwise the chip is text-only — better than linking to a 404
  // and more honest than hiding the source entirely. reachable===undefined
  // (legacy or pre-Commit-C messages) is treated as unknown → text-only.
  const hasReachableUrl = source.url
    && /^https?:\/\//.test(source.url)
    && source.reachable === true;
  const Tag = hasReachableUrl ? 'a' : 'span';
  const tagProps = hasReachableUrl
    ? { href: source.url, target: '_blank', rel: 'noopener noreferrer' }
    : {};
  const linkColor = hasReachableUrl ? '#7a9e82' : '#888';
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
    >
      <Tag
        {...tagProps}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          fontSize: 10,
          fontFamily: 'Georgia, serif',
          color: linkColor,
          background: '#141414',
          border: '1px solid #2a2a2a',
          borderRadius: 4,
          padding: '3px 8px',
          textDecoration: 'none',
          cursor: hasReachableUrl ? 'pointer' : 'default',
        }}
      >
        <svg width="9" height="9" viewBox="0 0 16 16" fill="none">
          <path d="M3 3h7v2H5v6h6V7h2v6H3V3z" stroke="currentColor" strokeWidth="1.2" />
          <path d="M9 3h4v4" stroke="currentColor" strokeWidth="1.2" />
        </svg>
        {source.label}
      </Tag>
      {expanded && source.snippet && (
        <div style={{
          position: 'absolute',
          bottom: 'calc(100% + 6px)',
          left: 0,
          background: '#111',
          border: '1px solid #2a2a2a',
          borderRadius: 5,
          padding: '8px 11px',
          minWidth: 180,
          maxWidth: 320,
          fontSize: 10,
          color: '#a8a8a8',
          fontFamily: 'Georgia, serif',
          fontStyle: 'italic',
          lineHeight: 1.5,
          pointerEvents: 'none',
          zIndex: 30,
          boxShadow: '0 4px 14px rgba(0,0,0,0.6)',
        }}>
          {source.snippet}
        </div>
      )}
    </span>
  );
}

function ClaimCard({ payload }) {
  return (
    <>
      <div style={{
        fontSize: 14,
        lineHeight: 1.7,
        color: '#d4d4d4',
        fontFamily: 'Georgia, serif',
        marginBottom: 12,
        whiteSpace: 'pre-wrap',
      }}>
        {payload.answer_text}
      </div>
      {payload.caveats && (
        <div style={{
          fontSize: 11,
          color: '#888',
          fontFamily: 'Georgia, serif',
          fontStyle: 'italic',
          lineHeight: 1.6,
          marginBottom: 12,
          paddingLeft: 10,
          borderLeft: '2px solid #252525',
        }}>
          {payload.caveats}
        </div>
      )}
      {Array.isArray(payload.sources) && payload.sources.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {payload.sources.map((src, i) => (
            <SourceChip key={i} source={src} />
          ))}
        </div>
      )}
    </>
  );
}

function AnalysisCard({ payload }) {
  return (
    <>
      <div style={{
        fontSize: 13,
        lineHeight: 1.7,
        color: '#d4d4d4',
        fontFamily: 'Georgia, serif',
        marginBottom: 14,
        fontWeight: 500,
      }}>
        {payload.summary}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
        {payload.findings.map((f, i) => {
          const palette = CONFIDENCE_COLORS[f.confidence] || CONFIDENCE_COLORS.medium;
          return (
            <div key={i} style={{
              padding: '10px 12px',
              background: '#07070a',
              border: '1px solid #1e1e2a',
              borderLeft: `2px solid ${palette.color}`,
              borderRadius: '3px 6px 6px 3px',
            }}>
              <div style={{
                fontSize: 13,
                color: '#d4d4d4',
                fontFamily: 'Georgia, serif',
                lineHeight: 1.6,
                marginBottom: 6,
              }}>
                {f.claim}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <ConfidenceBadge level={f.confidence} />
                <span style={{
                  fontSize: 10,
                  color: '#666',
                  fontFamily: 'Georgia, serif',
                  fontStyle: 'italic',
                }}>
                  {f.evidence}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {Array.isArray(payload.open_questions) && payload.open_questions.length > 0 && (
        <div style={{
          padding: '10px 12px',
          background: 'rgba(155,143,196,0.04)',
          border: '1px solid rgba(155,143,196,0.18)',
          borderRadius: 5,
        }}>
          <div style={{
            fontSize: 9,
            color: '#9b8fc4',
            fontFamily: 'Georgia, serif',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginBottom: 6,
          }}>
            Still open
          </div>
          {payload.open_questions.map((q, i) => (
            <div key={i} style={{
              fontSize: 11,
              color: '#a8a8a8',
              fontFamily: 'Georgia, serif',
              lineHeight: 1.6,
              marginBottom: i < payload.open_questions.length - 1 ? 4 : 0,
            }}>
              · {q}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function VerificationBadge({ verification }) {
  const [expanded, setExpanded] = useState(false);
  if (!verification || !verification.status) return null;

  const palettes = {
    clean:     { color: '#7a9e82', bg: 'rgba(122,158,130,0.1)', border: 'rgba(122,158,130,0.35)', label: 'verified', icon: '✓' },
    flagged:   { color: '#9b8fc4', bg: 'rgba(155,143,196,0.1)', border: 'rgba(155,143,196,0.35)', label: `${verification.issues?.length || 1} caveat${(verification.issues?.length || 1) === 1 ? '' : 's'}`, icon: '!' },
    unchecked: { color: '#666',     bg: '#141414',                 border: '#2a2a2a',                 label: 'unchecked', icon: '?' },
  };
  const palette = palettes[verification.status] || palettes.unchecked;
  const hasIssues = verification.status === 'flagged' && Array.isArray(verification.issues) && verification.issues.length > 0;

  return (
    <span
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => hasIssues && setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
    >
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 9,
        fontFamily: 'Georgia, serif',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: palette.color,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: 4,
        padding: '3px 8px',
        cursor: hasIssues ? 'help' : 'default',
      }}>
        <span style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          background: palette.color,
          color: '#07070a',
          fontSize: 9,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          lineHeight: 1,
        }}>
          {palette.icon}
        </span>
        {palette.label}
      </span>
      {expanded && hasIssues && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          right: 0,
          background: '#111',
          border: '1px solid #2a2a2a',
          borderRadius: 5,
          padding: '8px 11px',
          minWidth: 220,
          maxWidth: 320,
          fontSize: 11,
          color: '#a8a8a8',
          fontFamily: 'Georgia, serif',
          lineHeight: 1.55,
          pointerEvents: 'none',
          zIndex: 30,
          boxShadow: '0 4px 14px rgba(0,0,0,0.6)',
        }}>
          <div style={{
            fontSize: 9,
            color: '#9b8fc4',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginBottom: 6,
          }}>
            Self-critique caveats
          </div>
          {verification.issues.map((issue, i) => (
            <div key={i} style={{ marginBottom: i < verification.issues.length - 1 ? 6 : 0 }}>
              · {issue}
            </div>
          ))}
        </div>
      )}
    </span>
  );
}

function GroundedAnswerCard({ payload }) {
  if (!payload || !payload.type) return null;
  const isClaim = payload.type === 'claim';
  const topConfidence = isClaim ? payload.confidence : null;

  return (
    <div style={{
      width: '100%',
      maxWidth: 640,
      background: '#07070a',
      border: '1px solid #272736',
      borderLeft: '2px solid #c8a96e',
      borderRadius: '4px 8px 8px 4px',
      padding: '14px 16px',
    }}>
      {/* Header — deep mode label + verification + confidence */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        marginBottom: 12,
        paddingBottom: 10,
        borderBottom: '1px solid #1e1e2a',
        flexWrap: 'wrap',
      }}>
        <span style={{
          fontSize: 9,
          color: '#c8a96e',
          fontFamily: 'Georgia, serif',
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}>
          <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="6" stroke="#c8a96e" strokeWidth="1.4" />
            <circle cx="8" cy="8" r="2.5" fill="#c8a96e" />
          </svg>
          Deep mode
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <VerificationBadge verification={payload.verification} />
          {topConfidence && <ConfidenceBadge level={topConfidence} />}
        </div>
      </div>

      {/* Body — claim or analysis */}
      {isClaim ? <ClaimCard payload={payload} /> : <AnalysisCard payload={payload} />}

      {/* Footer — model used */}
      {payload.model_used && (
        <div style={{
          marginTop: 12,
          paddingTop: 10,
          borderTop: '1px solid #1e1e2a',
          fontSize: 9,
          color: '#444',
          fontFamily: 'Georgia, serif',
          letterSpacing: '0.04em',
          textAlign: 'right',
        }}>
          {payload.model_used}
        </div>
      )}
    </div>
  );
}

export default memo(GroundedAnswerCard);
